import { VALIDATION_VARIANTS } from '@gef-ui/material-theme-utils';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
const notchedOutlineColor = (color)=>({
        [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: color
        }
    });
const createOutlineInputStyle = (color)=>({
        color,
        ...notchedOutlineColor(color),
        [`&.${outlinedInputClasses.disabled},&:hover`]: notchedOutlineColor(color)
    });
const afterTransformVariants = VALIDATION_VARIANTS.map((variant)=>({
        props: {
            validation: variant
        },
        style: {
            '&:after': {
                transform: 'scaleX(1)'
            }
        }
    }));
const disabledTooltipIconColor = (color)=>({
        [`&.${inputBaseClasses.disabled}`]: {
            '& .MuiSvgIcon-root': {
                color
            }
        }
    });
export default function Input(theme) {
    return {
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        '& svg': {
                            color: theme.palette.text.disabled
                        }
                    },
                    variants: VALIDATION_VARIANTS.map((variant)=>({
                            props: {
                                validation: variant
                            },
                            style: disabledTooltipIconColor(theme.palette[variant].main)
                        }))
                },
                input: {
                    '&::placeholder': {
                        opacity: 1,
                        color: theme.palette.text.disabled
                    },
                    '&.Mui-disabled': {
                        color: theme.palette.text.secondary
                    }
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    variants: afterTransformVariants
                },
                underline: {
                    '&:before': {
                        borderBottomColor: theme.palette.grey[500_56]
                    }
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: ({ ownerState })=>{
                    const variants = [
                        ...VALIDATION_VARIANTS.map((variant)=>({
                                props: {
                                    validation: variant,
                                    'data-gef-variant': 'static'
                                },
                                style: {
                                    borderColor: theme.palette[variant].main,
                                    backgroundColor: theme.palette[variant].shades['8p'],
                                    color: theme.palette[variant].main,
                                    '&:hover': {
                                        borderColor: theme.palette[variant].main,
                                        backgroundColor: theme.palette[variant].shades['8p']
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: theme.palette[variant].shades['8p']
                                    }
                                }
                            })),
                        ...afterTransformVariants
                    ];
                    if (ownerState['data-gef-variant'] === 'static') {
                        return {
                            borderRadius: theme.radius(0.75),
                            borderStyle: 'solid',
                            borderWidth: 1,
                            '&:before': {
                                display: 'none'
                            },
                            '&:after': {
                                display: 'none'
                            },
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.grey[500_56],
                            '& .MuiInputAdornment-filled.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel), .MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)': {
                                marginTop: '0px'
                            },
                            '&:hover': {
                                backgroundColor: theme.palette.background.paper,
                                borderColor: theme.palette.text.primary
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme.palette.background.paper,
                                borderColor: theme.palette[ownerState.color].main,
                                boxShadow: `0 0 0 1px ${theme.palette[ownerState.color].main}`
                            },
                            '&.Mui-disabled': {
                                borderStyle: 'dashed',
                                backgroundColor: theme.palette.background.paper,
                                borderColor: theme.palette.action.disabled
                            },
                            '&.Mui-disabled.MuiInputBase-input': {
                                color: theme.palette.primary.main
                            },
                            variants
                        };
                    } else {
                        return {
                            backgroundColor: theme.palette.grey[500_12],
                            borderTopLeftRadius: theme.radius.small,
                            borderTopRightRadius: theme.radius.small,
                            '&:hover': {
                                backgroundColor: theme.palette.grey[500_16]
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme.palette.action.focus
                            },
                            '&.Mui-disabled': {
                                backgroundColor: theme.palette.action.disabledBackground
                            },
                            '&.Mui-disabled.MuiInputBase-input': {
                                color: theme.palette.primary.main
                            },
                            variants
                        };
                    }
                },
                input: ({ ownerState })=>{
                    if (ownerState['data-gef-variant'] === 'static') {
                        return {
                            paddingTop: '7.5px',
                            paddingBottom: '7.5px',
                            '&.MuiInputBase-inputSizeSmall': {
                                paddingTop: '3.5px',
                                paddingBottom: '3.5px'
                            }
                        };
                    }
                },
                multiline: ({ ownerState })=>{
                    if (ownerState['data-gef-variant'] === 'static') {
                        return {
                            paddingLeft: theme.spacing(1.5),
                            paddingRight: theme.spacing(1.5),
                            paddingTop: 0,
                            paddingBottom: 0
                        };
                    }
                },
                underline: ({ ownerState })=>{
                    if (ownerState['data-gef-variant'] !== 'static') {
                        return {
                            '&:before': {
                                borderBottomColor: theme.palette.grey[500_56]
                            }
                        };
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: theme.radius(0.5),
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.grey[500_32]
                    },
                    '&.Mui-disabled': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.action.disabledBackground,
                            borderStyle: 'dashed'
                        }
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled': {
                        borderColor: theme.palette.primary.main
                    },
                    variants: [
                        ...VALIDATION_VARIANTS.map((variant)=>({
                                props: {
                                    validation: variant
                                },
                                style: createOutlineInputStyle(theme.palette[variant].main)
                            })),
                        {
                            props: {
                                datagrid: true
                            },
                            style: {
                                [`& .${outlinedInputClasses.notchedOutline}`]: {
                                    borderStyle: 'hidden'
                                }
                            }
                        }
                    ]
                }
            }
        }
    };
}
